/**
 * Categories Controller Module
 * Handles loading pure HTML template partials, categories schema validation, rich layout rendering, raw JSON viewer toggling, and Firestore persistence.
 */
import { BUTTON, CARD } from "./ui/classes.js";
import { showDialog, hideDialog } from "./ui/dialog.js";
import { solidIcon } from "./ui/icons.js";
import { jsArg } from "./ui/copy.js";

export async function loadCategoriesTemplate() {
  const container = document.getElementById("sectionCategoriesContainer");
  if (!container) return;

  if (document.getElementById("sectionCategories")) return;

  try {
    const resp = await fetch("templates/categories.html");
    if (!resp.ok) {
      throw new Error(`Failed to load categories template: ${resp.status} ${resp.statusText}`);
    }
    const html = await resp.text();
    container.innerHTML = html;
  } catch (err) {
    console.error("❌ Categories Template Load Error:", err);
    container.innerHTML = `<div class="rounded-md bg-red-50 p-4 text-sm text-red-700 dark:bg-red-500/10 dark:text-red-300">Could not load the Categories template: ${escapeHtml(err.message)}</div>`;
  }
}

export function validateCategoriesDocument(docData) {
  const issues = [];
  const snakeCaseRegex = /^[a-z0-9]+(_[a-z0-9]+)*$/;

  if (!docData || typeof docData !== "object") {
    return ["Categories document is missing or invalid object."];
  }

  // 1. Validate dietary_preferences
  if (!Array.isArray(docData.dietary_preferences)) {
    issues.push("Missing or invalid 'dietary_preferences' array.");
  } else {
    docData.dietary_preferences.forEach((pref, idx) => {
      if (!pref.id) issues.push(`dietary_preferences[${idx}] missing 'id'`);
      else if (!snakeCaseRegex.test(pref.id)) issues.push(`dietary_preferences[${idx}].id '${pref.id}' is not snake_case`);
      if (!pref.name) issues.push(`dietary_preferences[${idx}] missing 'name'`);
      if (!pref.emoji) issues.push(`dietary_preferences[${idx}] missing 'emoji'`);
    });
  }

  // 2. Validate allergens
  if (!Array.isArray(docData.allergens)) {
    issues.push("Missing or invalid 'allergens' array.");
  } else {
    docData.allergens.forEach((alg, idx) => {
      if (!alg.id) issues.push(`allergens[${idx}] missing 'id'`);
      else if (!snakeCaseRegex.test(alg.id)) issues.push(`allergens[${idx}].id '${alg.id}' is not snake_case`);
      if (!alg.name) issues.push(`allergens[${idx}] missing 'name'`);
      if (!alg.emoji) issues.push(`allergens[${idx}] missing 'emoji'`);
    });
  }

  // 3. Validate ingredient_categories
  if (!Array.isArray(docData.ingredient_categories)) {
    issues.push("Missing or invalid 'ingredient_categories' array.");
  } else {
    docData.ingredient_categories.forEach((cat, idx) => {
      if (!cat.code) issues.push(`ingredient_categories[${idx}] missing 'code'`);
      if (!cat.name) issues.push(`ingredient_categories[${idx}] missing 'name'`);
      if (!Array.isArray(cat.subgroups)) issues.push(`ingredient_categories[${idx}] '${cat.name}' missing 'subgroups'`);
    });
  }

  // 4. Validate recipe_categories
  if (!Array.isArray(docData.recipe_categories)) {
    issues.push("Missing or invalid 'recipe_categories' array.");
  } else {
    docData.recipe_categories.forEach((cat, idx) => {
      if (!cat.category_id) issues.push(`recipe_categories[${idx}] missing 'category_id'`);
      if (!cat.name) issues.push(`recipe_categories[${idx}] missing 'name'`);
      if (!Array.isArray(cat.tags)) issues.push(`recipe_categories[${idx}] '${cat.name}' missing 'tags'`);
    });
  }

  return issues;
}

export function toggleCategoriesRawJson(categoriesData) {
  const viewer = document.getElementById("categoriesJsonViewer");
  const pre = document.getElementById("rawCategoriesJsonContent");
  if (!viewer || !pre) return;

  if (viewer.style.display === "block") {
    viewer.style.display = "none";
  } else {
    pre.textContent = JSON.stringify(categoriesData, null, 2);
    viewer.style.display = "block";
  }
}

let activeCategoriesDataRef = null;


export function updateActiveCategoriesData(categoriesData) {
  activeCategoriesDataRef = categoriesData;
}

/** The document the Categories screen shows and edits, which Save writes. */
export function getActiveCategoriesData() {
  return activeCategoriesDataRef;
}

function normalizeTagObject(t) {
  if (typeof t === "string") {
    const cleanId = t.trim();
    const formattedName = cleanId.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
    return { id: cleanId, name: formattedName, is_featured: false };
  }
  if (t && typeof t === "object") {
    const id = String(t.id || t.code || t.tag_id || t.name || "").trim();
    const name = t.name || t.label || t.title || id;
    return { ...t, id, name, is_featured: Boolean(t.is_featured) };
  }
  return { id: "", name: "", is_featured: false };
}

export function toggleCategoryFeatured(sectionKey, itemId, subId = null) {
  console.log("⭐ [toggleCategoryFeatured] Called:", { sectionKey, itemId, subId });
  if (!activeCategoriesDataRef) {
    console.error("❌ activeCategoriesDataRef is null in toggleCategoryFeatured!");
    return;
  }
  const data = activeCategoriesDataRef;

  const cleanItemId = String(itemId || "").trim().toLowerCase();
  const cleanSubId = String(subId || "").trim().toLowerCase();

  if (sectionKey === "dietary_preferences" || sectionKey === "allergens") {
    const list = data[sectionKey] || [];
    const item = list.find((x) => String(x.id).trim().toLowerCase() === cleanItemId);
    if (item) item.is_featured = !Boolean(item.is_featured);
  } else if (sectionKey === "ingredient_categories") {
    const list = data.ingredient_categories || [];
    const cat = list.find((x) => String(x.code || x.id).trim().toLowerCase() === cleanItemId);
    if (cat) {
      if (cleanSubId) {
        if (!Array.isArray(cat.subgroups)) cat.subgroups = [];
        cat.subgroups = cat.subgroups.map((s) => typeof s === "string" ? { code: s, name: s, is_featured: false } : s);
        const sub = cat.subgroups.find((s) => String(s.code || s.id).trim().toLowerCase() === cleanSubId);
        if (sub) sub.is_featured = !Boolean(sub.is_featured);
      } else {
        cat.is_featured = !Boolean(cat.is_featured);
      }
    }
  } else if (sectionKey === "recipe_categories") {
    const list = data.recipe_categories || [];
    const cat = list.find((x) => String(x.category_id || x.id).trim().toLowerCase() === cleanItemId);
    if (cat) {
      if (cleanSubId) {
        if (!Array.isArray(cat.tags)) cat.tags = [];
        const tag = cat.tags.find((t) => {
          const tid = String(t.id || t.code || "").trim().toLowerCase();
          return tid === cleanSubId;
        });
        if (tag) {
          const oldVal = tag.is_featured;
          tag.is_featured = !Boolean(tag.is_featured);
          console.log(`⭐ [toggleCategoryFeatured] Tag '${cleanSubId}' toggled: ${oldVal} → ${tag.is_featured}`);
        } else {
          console.warn(`⭐ [toggleCategoryFeatured] Tag '${cleanSubId}' NOT found in cat '${cleanItemId}'`);
        }
      } else {
        cat.is_featured = !Boolean(cat.is_featured);
      }
    }
  }

  renderCategoriesView(data);
  // Save the document just changed: app.js's own copy is a different object after any save (#1210).
  if (window.crmApp && window.crmApp.saveCategoriesDocument) {
    window.crmApp.saveCategoriesDocument(data);
  }
}

export function deleteCategoryItem(sectionKey, itemId, subId = null) {
  console.log("🗑️ [deleteCategoryItem] Called:", { sectionKey, itemId, subId });
  if (!activeCategoriesDataRef) return;
  const data = activeCategoriesDataRef;

  const targetName = subId || itemId;
  if (!confirm(`Delete item '${targetName}'?`)) return;

  const cleanItemId = String(itemId || "").trim().toLowerCase();
  const cleanSubId = String(subId || "").trim().toLowerCase();

  if (sectionKey === "dietary_preferences" || sectionKey === "allergens") {
    data[sectionKey] = (data[sectionKey] || []).filter((x) => String(x.id).trim().toLowerCase() !== cleanItemId);
  } else if (sectionKey === "ingredient_categories") {
    const list = data.ingredient_categories || [];
    if (cleanSubId) {
      const cat = list.find((x) => String(x.code || x.id).trim().toLowerCase() === cleanItemId);
      if (cat) {
        cat.subgroups = (cat.subgroups || []).filter((s) => String(s.code || s.id).trim().toLowerCase() !== cleanSubId);
      }
    } else {
      data.ingredient_categories = list.filter((x) => String(x.code || x.id).trim().toLowerCase() !== cleanItemId);
    }
  } else if (sectionKey === "recipe_categories") {
    const list = data.recipe_categories || [];
    if (cleanSubId) {
      const cat = list.find((x) => String(x.category_id || x.id).trim().toLowerCase() === cleanItemId);
      if (cat) {
        cat.tags = (cat.tags || []).filter((t) => {
          const tid = typeof t === "string" ? t : (t.id || t.code);
          return String(tid).trim().toLowerCase() !== cleanSubId;
        });
      }
    } else {
      data.recipe_categories = list.filter((x) => String(x.category_id || x.id).trim().toLowerCase() !== cleanItemId);
    }
  }

  renderCategoriesView(data);
  // Save the document just changed: app.js's own copy is a different object after any save (#1210).
  if (window.crmApp && window.crmApp.saveCategoriesDocument) {
    window.crmApp.saveCategoriesDocument(data);
  }
}

export function openCategoryModal(sectionKey, itemId = null, subId = null, isEdit = false) {
  console.log("📂 [openCategoryModal] Called:", { sectionKey, itemId, subId, isEdit });
  if (!activeCategoriesDataRef) {
    console.error("❌ activeCategoriesDataRef is null!");
    alert("Categories data not loaded yet. Please try again in a moment.");
    return;
  }
  const data = activeCategoriesDataRef;

  const modal = document.getElementById("categoryModal");
  if (!modal) {
    console.error("❌ categoryModal element not found in DOM!");
    alert("Category modal element missing!");
    return;
  }

  document.getElementById("catModalSectionKey").value = sectionKey || "";
  document.getElementById("catModalParentId").value = itemId || "";
  document.getElementById("catModalSubId").value = subId || "";
  document.getElementById("catModalIsEdit").value = isEdit ? "true" : "false";

  const titleEl = document.getElementById("categoryModalTitle");
  const idInput = document.getElementById("catModalId");
  const nameInput = document.getElementById("catModalName");
  const emojiInput = document.getElementById("catModalEmoji");
  const descInput = document.getElementById("catModalDesc");
  const featuredCb = document.getElementById("catModalFeatured");

  const emojiGroup = document.getElementById("catModalEmojiGroup");
  const descGroup = document.getElementById("catModalDescGroup");

  if (sectionKey === "dietary_preferences" || sectionKey === "allergens") {
    if (emojiGroup) emojiGroup.style.display = "block";
    if (descGroup) descGroup.style.display = "block";
  } else {
    if (emojiGroup) emojiGroup.style.display = "none";
    if (descGroup) descGroup.style.display = "block";
  }

  const cleanItemId = String(itemId || "").trim().toLowerCase();
  const cleanSubId = String(subId || "").trim().toLowerCase();

  const featuredGroup = document.getElementById("catModalFeaturedGroup");
  const isRecipeTag = sectionKey === "recipe_categories" && (subId !== null || (!isEdit && itemId !== null));
  if (featuredGroup) {
    featuredGroup.style.display = isRecipeTag ? "flex" : "none";
  }

  if (isEdit) {
    let targetItem = null;
    const activeId = subId || itemId;

    if (sectionKey === "dietary_preferences" || sectionKey === "allergens") {
      targetItem = (data[sectionKey] || []).find((x) => String(x.id).trim().toLowerCase() === cleanItemId);
    } else if (sectionKey === "ingredient_categories") {
      const cat = (data.ingredient_categories || []).find((x) => String(x.code || x.id).trim().toLowerCase() === cleanItemId);
      if (cleanSubId && cat) {
        cat.subgroups = (cat.subgroups || []).map((s) => typeof s === "string" ? { code: s, name: s, is_featured: false } : s);
        targetItem = cat.subgroups.find((s) => String(s.code || s.id).trim().toLowerCase() === cleanSubId);
      } else {
        targetItem = cat;
      }
    } else if (sectionKey === "recipe_categories") {
      const cat = (data.recipe_categories || []).find((x) => String(x.category_id || x.id).trim().toLowerCase() === cleanItemId);
      if (cleanSubId && cat) {
        cat.tags = (cat.tags || []).map(normalizeTagObject);
        targetItem = cat.tags.find((t) => String(t.id || t.code).trim().toLowerCase() === cleanSubId);
      } else {
        targetItem = cat;
      }
    }

    const displayName = targetItem?.name || activeId;
    titleEl.textContent = `Edit ${displayName}`;
    idInput.value = activeId;
    idInput.readOnly = true;

    if (targetItem) {
      nameInput.value = targetItem.name || "";
      emojiInput.value = targetItem.emoji || "";
      descInput.value = targetItem.description || "";
      featuredCb.checked = Boolean(targetItem.is_featured);
    }
  } else {
    const isAddingChild = Boolean(subId || itemId);
    const NEW_ITEM = { dietary_preferences: "dietary preference", allergens: "allergen", ingredient_categories: "aisle", recipe_categories: "recipe category" };
    const itemLabel = isAddingChild ? (sectionKey === "recipe_categories" ? "tag" : "subgroup") : (NEW_ITEM[sectionKey] || sectionKey.replace(/_/g, " "));
    titleEl.textContent = `New ${itemLabel}`;
    idInput.value = "";
    idInput.readOnly = false;

    nameInput.value = "";
    emojiInput.value = "";
    descInput.value = "";
    featuredCb.checked = false;
  }

  showDialog("categoryModal");
}

export function saveCategoryModalSubmit() {
  console.log("💾 [saveCategoryModalSubmit] Called");
  if (!activeCategoriesDataRef) return;
  const data = activeCategoriesDataRef;

  const sectionKey = document.getElementById("catModalSectionKey").value;
  const parentId = document.getElementById("catModalParentId").value.trim();
  const subId = document.getElementById("catModalSubId").value.trim();
  const isEdit = document.getElementById("catModalIsEdit").value === "true";

  const name = document.getElementById("catModalName").value.trim();
  const emoji = document.getElementById("catModalEmoji").value.trim();
  const description = document.getElementById("catModalDesc").value.trim();
  const is_featured = document.getElementById("catModalFeatured").checked;

  if (!name) {
    alert("Name is required!");
    return;
  }

  const cleanParentId = parentId.toLowerCase();
  const cleanSubId = subId.toLowerCase();

  // For EDIT mode, target ID is immutable (either subId or parentId)
  // For ADD mode, target ID is derived from name if not provided
  let targetId = "";
  if (isEdit) {
    targetId = subId || parentId;
  } else {
    const userDefinedId = document.getElementById("catModalId").value.trim();
    targetId = userDefinedId ? userDefinedId.toLowerCase().replace(/[^a-z0-9_]+/g, "_") : name.toLowerCase().replace(/[^a-z0-9_]+/g, "_");
  }
  const cleanTargetId = targetId.toLowerCase();

  console.log("💾 [saveCategoryModalSubmit] Processing:", { sectionKey, parentId, subId, isEdit, targetId, name, is_featured });

  if (sectionKey === "dietary_preferences" || sectionKey === "allergens") {
    if (!Array.isArray(data[sectionKey])) data[sectionKey] = [];
    if (isEdit) {
      const item = data[sectionKey].find((x) => String(x.id).trim().toLowerCase() === cleanTargetId);
      if (item) {
        item.name = name;
        item.emoji = emoji;
        item.description = description;
      }
    } else {
      if (data[sectionKey].some((x) => String(x.id).trim().toLowerCase() === cleanTargetId)) {
        alert(`Item with ID/Name '${name}' already exists!`);
        return;
      }
      const newItem = { id: targetId, name };
      if (emoji) newItem.emoji = emoji;
      if (description) newItem.description = description;
      data[sectionKey].push(newItem);
    }
  } else if (sectionKey === "ingredient_categories") {
    if (!Array.isArray(data.ingredient_categories)) data.ingredient_categories = [];

    // Is child subgroup? (subId present on Edit, or parentId present on Add child)
    const isSubgroup = isEdit ? Boolean(subId) : Boolean(parentId);

    if (isSubgroup) {
      const cat = data.ingredient_categories.find((x) => String(x.code || x.id).trim().toLowerCase() === cleanParentId);
      if (cat) {
        if (!Array.isArray(cat.subgroups)) cat.subgroups = [];
        cat.subgroups = cat.subgroups.map((s) => typeof s === "string" ? { code: s, name: s, is_featured: false } : s);

        if (isEdit) {
          const sub = cat.subgroups.find((s) => String(s.code || s.id).trim().toLowerCase() === cleanTargetId);
          if (sub) {
            sub.name = name;
          }
        } else {
          if (cat.subgroups.some((s) => String(s.code || s.id).trim().toLowerCase() === cleanTargetId)) {
            alert(`Subgroup '${name}' already exists in category '${cat.name}'!`);
            return;
          }
          cat.subgroups.push({ code: targetId, name });
        }
      }
    } else {
      if (isEdit) {
        const cat = data.ingredient_categories.find((x) => String(x.code || x.id).trim().toLowerCase() === cleanTargetId);
        if (cat) {
          cat.name = name;
          cat.description = description;
        }
      } else {
        if (data.ingredient_categories.some((x) => String(x.code || x.id).trim().toLowerCase() === cleanTargetId)) {
          alert(`Category '${name}' already exists!`);
          return;
        }
        data.ingredient_categories.push({ code: targetId, name, description, subgroups: [] });
      }
    }
  } else if (sectionKey === "recipe_categories") {
    if (!Array.isArray(data.recipe_categories)) data.recipe_categories = [];

    // Is child tag? (subId present on Edit, or parentId present on Add child)
    const isTag = isEdit ? Boolean(subId) : Boolean(parentId);

    if (isTag) {
      const cat = data.recipe_categories.find((x) => String(x.category_id || x.id).trim().toLowerCase() === cleanParentId);
      if (cat) {
        if (!Array.isArray(cat.tags)) cat.tags = [];
        cat.tags = cat.tags.map(normalizeTagObject);

        if (isEdit) {
          const tag = cat.tags.find((t) => String(t.id || t.code).trim().toLowerCase() === cleanTargetId);
          if (tag) {
            tag.name = name;
            tag.is_featured = is_featured;
          }
        } else {
          if (cat.tags.some((t) => String(t.id || t.code).trim().toLowerCase() === cleanTargetId)) {
            alert(`Tag '${name}' already exists in category '${cat.name}'!`);
            return;
          }
          cat.tags.push({ id: targetId, name, is_featured });
        }
      }
    } else {
      if (isEdit) {
        const cat = data.recipe_categories.find((x) => String(x.category_id || x.id).trim().toLowerCase() === cleanTargetId);
        if (cat) {
          cat.name = name;
          cat.description = description;
        }
      } else {
        if (data.recipe_categories.some((x) => String(x.category_id || x.id).trim().toLowerCase() === cleanTargetId)) {
          alert(`Category '${name}' already exists!`);
          return;
        }
        data.recipe_categories.push({ category_id: targetId, name, description, tags: [] });
      }
    }
  }

  hideDialog("categoryModal");
  renderCategoriesView(data);
  if (window.crmApp && window.crmApp.saveCategoriesDocument) {
    window.crmApp.saveCategoriesDocument(data);
  }
}

// Tailwind class lists for the lists below (#1210); whole strings, so Tailwind finds them.
const ICON_BUTTON = "rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 focus-visible:outline-2 focus-visible:outline-paprika-600 dark:text-gray-500 dark:hover:bg-white/10 dark:hover:text-white";
const ICON_DELETE_BUTTON = "rounded-md p-1 text-gray-400 hover:bg-red-50 hover:text-red-600 focus-visible:outline-2 focus-visible:outline-red-600 dark:text-gray-500 dark:hover:bg-red-500/10 dark:hover:text-red-400";
const ITEM_CARD = "flex items-start gap-3 rounded-lg bg-white p-4 shadow-xs outline-1 outline-black/5 dark:bg-gray-800/50 dark:shadow-none dark:-outline-offset-1 dark:outline-white/10";
const MUTED = "text-sm text-gray-500 dark:text-gray-400";

// A JS string argument inside a double-quoted onclick attribute.
const arg = jsArg;

function editButton(label, call) {
  return `<button type="button" aria-label="Edit ${escapeHtml(label)}" onclick="${call}" class="${ICON_BUTTON}">${solidIcon("pencil-square", "size-4")}</button>`;
}

function deleteButton(label, call) {
  return `<button type="button" aria-label="Delete ${escapeHtml(label)}" onclick="${call}" class="${ICON_DELETE_BUTTON}">${solidIcon("trash", "size-4")}</button>`;
}

function sectionHtml(title, count, addLabel, addCall, body) {
  return `
    <section>
      <div class="flex items-center justify-between gap-4">
        <h3 class="text-base font-semibold text-gray-900 dark:text-white">${title} <span class="ml-1 text-sm font-normal text-gray-500 dark:text-gray-400">${count}</span></h3>
        <button type="button" onclick="${addCall}" class="${BUTTON.small}">${solidIcon("plus", "-ml-0.5 size-4 text-gray-400")}${addLabel}</button>
      </div>
      ${body}
    </section>`;
}

function emptyListHtml(text) {
  return `<p class="mt-3 ${MUTED}">${text}</p>`;
}

// Diets and allergens: emoji, name and description, edited in the dialog.
function simpleListHtml(sectionKey, list) {
  if (!list.length) return emptyListHtml("None yet.");
  return `<ul role="list" class="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">${list.map((item) => `
    <li class="${ITEM_CARD}">
      <span class="w-7 flex-none text-center text-xl/6" aria-hidden="true">${escapeHtml(item.emoji || "")}</span>
      <div class="min-w-0 flex-1">
        <p class="text-sm/6 font-semibold text-gray-900 dark:text-white">${escapeHtml(item.name)}</p>
        <p class="mt-0.5 ${MUTED}">${escapeHtml(item.description || "")}</p>
      </div>
      <div class="-mt-0.5 -mr-1 flex flex-none">
        ${editButton(item.name, `window.crmApp.openCategoryModal(${arg(sectionKey)}, ${arg(item.id)}, null, true)`)}
        ${deleteButton(item.name, `window.crmApp.deleteCategoryItem(${arg(sectionKey)}, ${arg(item.id)})`)}
      </div>
    </li>`).join("")}</ul>`;
}

// A category card: name, description and actions on top, its children below, "Add" at the foot.
function groupCardHtml(sectionKey, id, group, childrenHtml, addLabel) {
  return `
    <div class="flex flex-col ${CARD.panel}">
      <div class="flex items-start justify-between gap-3 border-b border-gray-200 px-4 py-3 dark:border-white/10">
        <div class="min-w-0">
          <h4 class="text-sm/6 font-semibold text-gray-900 dark:text-white">${escapeHtml(group.name)}</h4>
          ${group.description ? `<p class="${MUTED}">${escapeHtml(group.description)}</p>` : ""}
        </div>
        <div class="-mr-1 flex flex-none">
          ${editButton(group.name, `window.crmApp.openCategoryModal(${arg(sectionKey)}, ${arg(id)}, null, true)`)}
          ${deleteButton(group.name, `window.crmApp.deleteCategoryItem(${arg(sectionKey)}, ${arg(id)})`)}
        </div>
      </div>
      <div class="flex-1">${childrenHtml}</div>
      <div class="border-t border-gray-200 px-4 py-2 dark:border-white/10">
        <button type="button" onclick="window.crmApp.openCategoryModal(${arg(sectionKey)}, ${arg(id)}, null, false)" class="${BUTTON.link} inline-flex items-center gap-x-1 text-sm">${solidIcon("plus", "size-4")}${addLabel}</button>
      </div>
    </div>`;
}

function subgroupsHtml(catCode, subgroups) {
  if (!subgroups.length) return `<p class="px-4 py-3 ${MUTED}">No subgroups yet.</p>`;
  return `<ul role="list" class="divide-y divide-gray-100 dark:divide-white/5">${subgroups.map((sub) => {
    const subCode = sub.code || sub.id;
    const name = sub.name || subCode;
    return `
      <li class="flex items-center justify-between gap-3 px-4 py-2">
        <div class="min-w-0">
          <p class="text-sm text-gray-900 dark:text-white">${escapeHtml(name)}</p>
          ${sub.examples?.length ? `<p class="truncate text-xs text-gray-500 dark:text-gray-400">${escapeHtml(sub.examples.join(", "))}</p>` : ""}
        </div>
        <div class="-mr-1 flex flex-none">
          ${editButton(name, `window.crmApp.openCategoryModal('ingredient_categories', ${arg(catCode)}, ${arg(subCode)}, true)`)}
          ${deleteButton(name, `window.crmApp.deleteCategoryItem('ingredient_categories', ${arg(catCode)}, ${arg(subCode)})`)}
        </div>
      </li>`;
  }).join("")}</ul>`;
}

// A tag: the star toggles Featured, the name opens the dialog, × deletes it.
function tagsHtml(catId, tags) {
  if (!tags.length) return `<p class="px-4 py-3 ${MUTED}">No tags yet.</p>`;
  return `<ul role="list" class="flex flex-wrap gap-1.5 px-4 py-3">${tags.map((t) => {
    const tagId = t.id || t.code;
    return `
      <li class="inline-flex items-center rounded-md bg-gray-50 text-xs font-medium text-gray-700 inset-ring inset-ring-gray-500/10 dark:bg-white/5 dark:text-gray-300 dark:inset-ring-white/10">
        <button type="button" aria-pressed="${t.is_featured}" aria-label="Featured: ${escapeHtml(t.name)}" onclick="window.crmApp.toggleCategoryFeatured('recipe_categories', ${arg(catId)}, ${arg(tagId)})" class="group rounded-l-md py-1 pr-0.5 pl-1.5 text-gray-300 hover:text-amber-400 aria-pressed:text-amber-400 dark:text-gray-600 dark:aria-pressed:text-amber-400">${solidIcon("star", "size-3.5")}</button>
        <button type="button" onclick="window.crmApp.openCategoryModal('recipe_categories', ${arg(catId)}, ${arg(tagId)}, true)" class="px-1 py-1 hover:text-gray-900 hover:underline dark:hover:text-white">${escapeHtml(t.name)}</button>
        <button type="button" aria-label="Delete ${escapeHtml(t.name)}" onclick="window.crmApp.deleteCategoryItem('recipe_categories', ${arg(catId)}, ${arg(tagId)})" class="rounded-r-md py-1 pr-1.5 pl-0.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400">${solidIcon("x-mark", "size-3.5")}</button>
      </li>`;
  }).join("")}</ul>`;
}

export function renderCategoriesView(categoriesData) {
  if (categoriesData) updateActiveCategoriesData(categoriesData);

  const container = document.getElementById("categoriesContainer");
  const pre = document.getElementById("rawCategoriesJsonContent");
  if (pre && categoriesData) {
    pre.textContent = JSON.stringify(categoriesData, null, 2);
  }

  if (!container) return;

  if (!categoriesData || Object.keys(categoriesData).length === 0) {
    container.innerHTML = `<p class="py-10 text-center ${MUTED}">Loading globals/categories…</p>`;
    return;
  }

  const issues = validateCategoriesDocument(categoriesData);
  const validationHtml = issues.length === 0
    ? `<div class="rounded-md bg-green-50 p-4 dark:bg-green-500/10 dark:outline dark:outline-green-500/20">
        <div class="flex gap-3">${solidIcon("check-circle", "size-5 flex-none text-green-400")}
          <p class="text-sm font-medium text-green-800 dark:text-green-300">The document passes the schema checks.</p>
        </div>
      </div>`
    : `<div class="rounded-md bg-yellow-50 p-4 dark:bg-yellow-500/10 dark:outline dark:outline-yellow-500/15">
        <div class="flex gap-3">${solidIcon("exclamation-triangle", "size-5 flex-none text-yellow-400")}
          <div>
            <h3 class="text-sm font-medium text-yellow-800 dark:text-yellow-100">${issues.length === 1 ? "1 problem" : `${issues.length} problems`} in the document</h3>
            <ul role="list" class="mt-2 list-disc space-y-1 pl-5 text-sm text-yellow-700 dark:text-yellow-100/80">${issues.map((iss) => `<li>${escapeHtml(iss)}</li>`).join("")}</ul>
          </div>
        </div>
      </div>`;

  const dietaryList = categoriesData.dietary_preferences || [];
  const allergensList = categoriesData.allergens || [];
  const ingCatList = categoriesData.ingredient_categories || [];
  const recipeCatList = categoriesData.recipe_categories || [];

  const aislesHtml = ingCatList.length
    ? `<div class="mt-3 grid grid-cols-1 gap-4 lg:grid-cols-2 2xl:grid-cols-3">${ingCatList.map((cat) => {
        const catCode = cat.code || cat.id;
        return groupCardHtml("ingredient_categories", catCode, cat, subgroupsHtml(catCode, cat.subgroups || []), "Add subgroup");
      }).join("")}</div>`
    : emptyListHtml("None yet.");

  const recipeCatsHtml = recipeCatList.length
    ? `<div class="mt-3 grid grid-cols-1 gap-4 lg:grid-cols-2 2xl:grid-cols-3">${recipeCatList.map((cat) => {
        const catId = cat.category_id || cat.id;
        return groupCardHtml("recipe_categories", catId, cat, tagsHtml(catId, (cat.tags || []).map(normalizeTagObject)), "Add tag");
      }).join("")}</div>`
    : emptyListHtml("None yet.");

  container.innerHTML = [
    validationHtml,
    sectionHtml("Dietary preferences", dietaryList.length, "Add preference", "window.crmApp.openCategoryModal('dietary_preferences', null, null, false)", simpleListHtml("dietary_preferences", dietaryList)),
    sectionHtml("Allergens", allergensList.length, "Add allergen", "window.crmApp.openCategoryModal('allergens', null, null, false)", simpleListHtml("allergens", allergensList)),
    sectionHtml("Supermarket aisles", ingCatList.length, "Add aisle", "window.crmApp.openCategoryModal('ingredient_categories', null, null, false)", aislesHtml),
    sectionHtml("Recipe categories and tags", recipeCatList.length, "Add category", "window.crmApp.openCategoryModal('recipe_categories', null, null, false)", recipeCatsHtml)
  ].join("");
}

export async function saveCategoriesDocument(db, categoriesData, setDoc, doc, showToast, dismissToast) {
  const issues = validateCategoriesDocument(categoriesData);
  if (issues.length > 0) {
    if (!confirm(`Warning: Found ${issues.length} schema validation issue(s). Do you still want to save?`)) {
      return;
    }
  }

  categoriesData.updated_at = new Date().toISOString();
  const processingToast = showToast("⏳ Saving globals/categories document to Firestore...", "processing");

  try {
    await setDoc(doc(db, "globals", "categories"), categoriesData);
    dismissToast(processingToast);
    renderCategoriesView(categoriesData);
    showToast("✓ Successfully saved categories document!", "success");
  } catch (err) {
    dismissToast(processingToast);
    console.error("❌ Save Categories Error:", err);
    showToast("❌ Failed to save categories: " + err.message, "danger");
  }
}

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
